/**
 * VEXOS Production Timeline — frontend mirror of `backend/routers/vexos_timeline.py`.
 *
 * Pure functions only. Durations are fetched once from
 * `GET /api/vexos-timeline/durations` on calculator mount and passed in to
 * `computeVexosTimeline()` so the live walkthrough card reflects every
 * change in selection without a server round-trip.
 *
 * Keep math IDENTICAL to the backend module so the live preview matches
 * what is persisted on save and rendered in the PDF.
 */
import { apiClient } from "../../App";
import { normalizeVideos, groupedVideos } from "./videoLineup";

// Fallback durations — match `_FALLBACK_DURATIONS` in vexos_timeline.py.
export const TIMELINE_FALLBACK_DURATIONS = {
  "VEXOS Platform Setup": 5,
  "VEXOS Landing Page": 3,
  "VEXOS Cover Landing Page": 3,
  "Exterior Orbit": 7,
  "Day-to-Night Upgrade": 2,
  "Animated Keyframes": 2,
  "Master Orbit": 5, // client-facing label is "Suborbit" — DB key unchanged
  "Exterior Rendering": 3,
  "Interior Rendering": 3,
  "Amenity Rendering": 3,
  "Drone & Reality Capture": 2,
  // VR Tours are billed per POINT (not per tour). A 10-point tour takes
  // significantly longer to capture, stitch, and QA than a 2-point tour.
  // The engine multiplies the per-point duration by the SUM of points
  // across all plans / amenity spaces. Admins can tune the per-point
  // value live in /studio/timeline-settings.
  "Interior VR Tour": 2,
  "Amenity VR Tour": 2,
  "AI Motion Video": 5,
  "AI Motion 30s": 10,
  "AI Motion 60s": 18,
  "AI Motion 90s": 22,
  "Cinematic Animation": 10,
  "Cinematic Film 30s": 14,
  "Cinematic Film 60s": 22,
  "Cinematic Film 90s": 30,
  "2D Floor Plan": 2,
  "3D Floor Plan": 3,
};

const RAMP_UP_BUFFER_DAYS = 5;
// Contractual VEXOS minimum delivery window (business days). Even a
// bare-bones platform engagement needs onboarding/integration QA/team
// coordination beyond the raw lane math. Mirrors `_VEXOS_MINIMUM_DAYS`
// in vexos_timeline.py — keep both in sync.
const VEXOS_MINIMUM_DAYS = 20;

// ── Staffing tiers — mirror backend DEFAULT_STAFFING_SETTINGS ────────
// Maps proposal total ($) → max concurrent artists for Studio Viz.
const STAFFING_BRACKETS = [
  { min: 0,      max: 20000,    maxArtists: 1 },
  { min: 20001,  max: 50000,    maxArtists: 2 },
  { min: 50001,  max: 100000,   maxArtists: 3 },
  { min: 100001, max: 150000,   maxArtists: 4 },
  { min: 150001, max: 200000,   maxArtists: 5 },
  { min: 200001, max: Infinity, maxArtists: 6 },
];
const STAFFING_FALLBACK = 2;

export function resolveStaffingAllowance(proposalTotal) {
  if (!proposalTotal || proposalTotal <= 0) return STAFFING_FALLBACK;
  for (const b of STAFFING_BRACKETS) {
    if (proposalTotal >= b.min && proposalTotal <= b.max) return b.maxArtists;
  }
  return STAFFING_BRACKETS[STAFFING_BRACKETS.length - 1].maxArtists;
}

// ── Async fetch wrapper ──────────────────────────────────────────────
export async function fetchTimelineDurations() {
  try {
    const res = await apiClient.get("/vexos-timeline/durations");
    const map = res?.data?.durations || {};
    // Merge with fallbacks so missing rows never produce 0d items.
    return { ...TIMELINE_FALLBACK_DURATIONS, ...map };
  } catch {
    return { ...TIMELINE_FALLBACK_DURATIONS };
  }
}

// ── Quantity helpers (match backend) ─────────────────────────────────
function sumValues(d) {
  if (!d || typeof d !== "object") return 0;
  let t = 0;
  for (const v of Object.values(d)) {
    const n = Number(v);
    if (Number.isFinite(n)) t += Math.floor(n);
  }
  return t;
}
function countYes(d) {
  if (!d || typeof d !== "object") return 0;
  let n = 0;
  for (const v of Object.values(d)) {
    if (v === true) n += 1;
    else if (typeof v === "string" && ["yes", "y", "true", "1"].includes(v.trim().toLowerCase())) n += 1;
  }
  return n;
}
function countSelected(d) {
  if (!d || typeof d !== "object") return 0;
  let n = 0;
  for (const v of Object.values(d)) {
    if (Number(v) > 0) n += 1;
  }
  return n;
}

// ── Client-facing viz ceiling ────────────────────────────────────────
// The Studio Visualization track never displays more than this many weeks
// (mirrors VIZ_MAX_WEEKS in backend/routers/vexos_timeline.py).
export const VIZ_MAX_WEEKS = 16;
export const VIZ_MAX_DAYS = VIZ_MAX_WEEKS * 5;

// ── Days → "~X–Y weeks" display ──────────────────────────────────────
export function daysToWeeksDisplay(days) {
  const d = Number(days) || 0;
  if (d <= 0) return "—";
  const weeksMin = Math.max(1, Math.floor(d / 5));
  const weeksMax = Math.max(1, Math.ceil(d / 5));
  return weeksMin === weeksMax
    ? `~${weeksMin} week${weeksMin > 1 ? "s" : ""}`
    : `~${weeksMin}–${weeksMax} weeks`;
}

// ── Selection → flat items normaliser ────────────────────────────────
// `selections` shape mirrors the calculator's per-mode input bundle.
//   { vexosLandingPage, vexosCoverLandingPage, dayToNight,
//     animatedKeyframes, animatedKeyframesCount, masterOrbits,
//     exteriorRenders, totalInteriorRenders, totalAmenityRenders,
//     interiorVrCount, amenityVrCount, aiMotion, cinematic,
//     total2D, total3D }
export function buildItemsFromInputs(inputs, projectType) {
  // SFH uses the condo field names; only community reads the c_ fields.
  const isCondo = (projectType || "condo").toLowerCase() !== "community";
  const f = (k, ck) => (isCondo ? inputs?.[k] : inputs?.[ck ?? `c_${k}`]);

  const dayToNight = Boolean(f("dayToNight", "c_dayToNight"));
  const animEnabled = Boolean(f("animatedKeyframesEnabled", "c_animatedKfEnabled"));
  const animQty = animEnabled ? Math.max(0, Math.floor(Number(f("animatedKeyframesQty", "c_animatedKfQty")) || 0)) : 0;
  const masterOrbits = Math.max(0, Math.floor(Number(f("masterOrbitsQty", "c_masterOrbitsQty")) || 0));
  // Conceptual renders are exterior stills with a different pipeline in
  // front of them, so they join the exterior rendering lane rather than
  // getting a lane (and a duration key) of their own.
  const exteriorRenders =
    Math.max(0, Math.floor(Number(f("exteriorRendersQty", "c_exteriorRendersQty")) || 0)) +
    Math.max(0, Math.floor(Number(f("conceptualRendersQty", "c_conceptualRendersQty")) || 0));

  const interiorRenders = f("interiorRenders", "c_homeModelRenders") || {};
  const interiorVR = f("interiorVR", "c_homeModelVR") || {};
  const amenityRenders = f("amenityRenders", "c_amenityRenders") || {};
  const amenityVR = f("amenityVR", "c_amenityVR") || {};

  const floor2D = f("floorPlans2D", "c_floorPlans2D") || {};
  const floor3D = f("floorPlans3D", "c_floorPlans3D") || {};

  // Drone capture toggle — gates the Lane B drone row. If no drone field
  // exists at all (legacy quotes saved before the toggle), default to
  // true so the row still shows. Otherwise honor whatever the calculator
  // saved.
  const DRONE_KEYS = [
    "droneCaptureEnabled", "c_droneCaptureEnabled",
    "interiorDroneShots", "amenityDroneShots",
    "c_interiorDroneShots", "c_amenityDroneShots",
  ];
  const droneKeyPresent = DRONE_KEYS.some((k) => k in (inputs || {}));
  let droneCaptureEnabled;
  if (!droneKeyPresent) {
    droneCaptureEnabled = true;
  } else if (isCondo) {
    droneCaptureEnabled = "droneCaptureEnabled" in inputs
      ? !!inputs.droneCaptureEnabled
      : (!!inputs.interiorDroneShots || !!inputs.amenityDroneShots);
  } else {
    droneCaptureEnabled = "c_droneCaptureEnabled" in inputs
      ? !!inputs.c_droneCaptureEnabled
      : (!!inputs.c_interiorDroneShots || !!inputs.c_amenityDroneShots);
  }

  // Video — surface BOTH the boolean (any selected) AND the duration
  // in seconds (30/60/90) so lane C math can pick the per-duration
  // deliverable row from the engine.
  const aiSecondsRaw = f("aiMotionDur", "c_aiMotionDur");
  const cineSecondsRaw = f("cinematicDur", "c_cinematicDur");
  const toSeconds = (v) => {
    if (v === null || v === undefined || v === "" || v === false) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  // Every video of each kind (several, mixed lengths) — see ./videoLineup.
  // Mirrors backend/routers/vexos_timeline.py _items_from_inputs.
  const aiMotionVideos = normalizeVideos(f("aiMotionVideos", "c_aiMotionVideos"), toSeconds(aiSecondsRaw));
  const cinematicVideos = normalizeVideos(f("cinematicVideos", "c_cinematicVideos"), toSeconds(cineSecondsRaw));
  const aiMotionDur = aiMotionVideos.length ? Math.max(...aiMotionVideos) : null;
  const cinematicDur = cinematicVideos.length ? Math.max(...cinematicVideos) : null;

  return {
    vexosLandingPage: Boolean(inputs?.vexosLandingPage || inputs?.c_vexosLandingPage),
    vexosCoverLandingPage: Boolean(f("vexosCoverLandingPage", "c_vexosCoverLandingPage")),
    dayToNight,
    animatedKeyframes: animEnabled && animQty > 0,
    animatedKeyframesCount: animQty,
    masterOrbits,
    exteriorRenders,
    totalInteriorRenders: sumValues(interiorRenders),
    totalAmenityRenders: sumValues(amenityRenders),
    interiorVrCount: countYes(interiorVR),
    amenityVrCount: countSelected(amenityVR),
    interiorVrPoints: sumValues(interiorVR),
    amenityVrPoints: sumValues(amenityVR),
    droneCaptureEnabled,
    aiMotion: aiMotionDur !== null,
    aiMotionDur,
    aiMotionVideos,
    cinematic: cinematicDur !== null,
    cinematicDur,
    cinematicVideos,
    total2D: sumValues(floor2D),
    total3D: sumValues(floor3D),
  };
}

// ── Lane math ────────────────────────────────────────────────────────
function calcVexos(items, dur) {
  // Lane 1 — Platform & landing pages (parallel)
  const setup = dur["VEXOS Platform Setup"] || 5;
  const landing = items.vexosLandingPage ? (dur["VEXOS Landing Page"] || 3) : 0;
  const cover = items.vexosCoverLandingPage ? (dur["VEXOS Cover Landing Page"] || 3) : 0;
  const lane1 = setup + Math.max(landing, cover);

  // Lane 2 — Orbit & enhancements (sequential with parallel branch)
  const dOrbit = dur["Exterior Orbit"] || 7;
  const dD2N = dur["Day-to-Night Upgrade"] || 2;
  const dAnim = dur["Animated Keyframes"] || 2;
  const dMaster = dur["Master Orbit"] || 5;
  const animQty = items.animatedKeyframes ? items.animatedKeyframesCount : 0;
  const masterQty = items.masterOrbits || 0;
  const lane2Branch = Math.max(
    items.dayToNight ? dD2N : 0,
    animQty > 0 ? dAnim * animQty : 0,
  );
  const lane2Master = masterQty > 0 ? dMaster * masterQty : 0;
  const lane2 = dOrbit + lane2Branch + lane2Master;

  const active = [];
  active.push({ name: "VEXOS Platform Setup", qty: 1, days: setup, lane: "platform" });
  if (landing) active.push({ name: "VEXOS Landing Page", qty: 1, days: landing, lane: "platform", parallel: cover > 0 });
  if (cover) active.push({ name: "VEXOS Cover Landing Page", qty: 1, days: cover, lane: "platform", parallel: landing > 0 });
  active.push({ name: "Exterior Orbit", qty: 1, days: dOrbit, lane: "orbit" });
  if (items.dayToNight) active.push({ name: "Day-to-Night Upgrade", qty: 1, days: dD2N, lane: "orbit", parallel: animQty > 0 });
  if (animQty > 0) active.push({ name: "Animated Keyframes", qty: animQty, days: dAnim * animQty, lane: "orbit", parallel: items.dayToNight });
  // Client-facing name is "Suborbit"; the duration stays keyed under the
  // "Master Orbit" deliverable in the Timeline Engine.
  if (masterQty > 0) active.push({ name: "Suborbit", qty: masterQty, days: dMaster * masterQty, lane: "orbit" });

  const naturalTotal = Math.max(lane1, lane2);
  // Apply VEXOS minimum floor (contractual baseline). Mirrors backend
  // `_VEXOS_MINIMUM_DAYS`. Surface the natural value + a flag so the
  // walkthrough card can show "floor applied" if desired.
  const total = Math.max(naturalTotal, VEXOS_MINIMUM_DAYS);
  const floorApplied = total > naturalTotal;
  let longestLane = lane2 >= lane1 ? "Lane 2 (Orbit)" : "Lane 1 (Platform)";
  if (floorApplied) longestLane = `Minimum floor (${VEXOS_MINIMUM_DAYS}d)`;
  return {
    days: total,
    naturalDays: naturalTotal,
    minimumDays: VEXOS_MINIMUM_DAYS,
    floorApplied,
    active,
    lanes: { platform: lane1, orbit: lane2 },
    longestLane,
  };
}

function calcViz(items, dur, maxArtists) {
  const dExtR = dur["Exterior Rendering"] || 3;
  const dIntR = dur["Interior Rendering"] || 3;
  const dAmR = dur["Amenity Rendering"] || 3;
  const dDrone = dur["Drone & Reality Capture"] || 2;
  const dIntVR = dur["Interior VR Tour"] || 2;
  const dAmVR = dur["Amenity VR Tour"] || 2;
  const dAI = dur["AI Motion Video"] || 5;
  const dCine = dur["Cinematic Animation"] || 10;
  // Per-duration video deliverables (Feb 2026 spec) — AI Motion 10/18/22d,
  // Cinematic Film 14/22/30d for 30s/60s/90s respectively.
  const dAI30 = dur["AI Motion 30s"] || 10;
  const dAI60 = dur["AI Motion 60s"] || 18;
  const dAI90 = dur["AI Motion 90s"] || 22;
  const dCine30 = dur["Cinematic Film 30s"] || 14;
  const dCine60 = dur["Cinematic Film 60s"] || 22;
  const dCine90 = dur["Cinematic Film 90s"] || 30;
  const d2D = dur["2D Floor Plan"] || 2;
  const d3D = dur["3D Floor Plan"] || 3;

  const active = [];

  // Lane A — Renderings (parallel; any active → 3d)
  const hasAnyRender = items.exteriorRenders > 0 || items.totalInteriorRenders > 0 || items.totalAmenityRenders > 0;
  const laneA = hasAnyRender ? Math.max(dExtR, dIntR, dAmR) : 0;
  if (items.exteriorRenders > 0)
    active.push({ name: "Exterior Rendering", qty: items.exteriorRenders, days: dExtR, lane: "renderings" });
  if (items.totalInteriorRenders > 0)
    active.push({ name: "Interior Rendering", qty: items.totalInteriorRenders, days: dIntR, lane: "renderings" });
  if (items.totalAmenityRenders > 0)
    active.push({ name: "Amenity Rendering", qty: items.totalAmenityRenders, days: dAmR, lane: "renderings" });

  // Lane B — VR
  // Duration scales by SUM OF POINTS (not tour count). A 10-point tour
  // therefore drives more lane time than two 2-point tours. Per-point
  // value is admin-tunable at /studio/timeline-settings.
  const intVrPoints = items.interiorVrPoints || 0;
  const amVrPoints = items.amenityVrPoints || 0;
  const anyVR = intVrPoints > 0 || amVrPoints > 0;
  // Drone capture only applies when ANY VR is selected AND the rep has
  // the unified Drone & Reality Capture toggle ON. Defaults to true for
  // backwards-compat with quotes saved before the field existed.
  const droneOn = items.droneCaptureEnabled !== false;
  const droneActive = anyVR && droneOn;
  const laneB = (droneActive ? dDrone : 0) + Math.max(
    dIntVR * intVrPoints,
    dAmVR * amVrPoints,
  );
  if (droneActive) active.push({ name: "Drone & Reality Capture", qty: 1, days: dDrone, lane: "vr" });
  if (intVrPoints > 0)
    active.push({
      name: "Interior VR Tour",
      qty: items.interiorVrCount,
      points: intVrPoints,
      days: dIntVR * intVrPoints,
      lane: "vr",
    });
  if (amVrPoints > 0)
    active.push({
      name: "Amenity VR Tour",
      qty: items.amenityVrCount,
      points: amVrPoints,
      days: dAmVR * amVrPoints,
      lane: "vr",
    });

  // Lane C — Video parallel
  // Picks the duration-specific deliverable (AI Motion 30/60/90s, Cinematic
  // Film 30/60/90s) based on the rep's selected video length. Falls back
  // to the legacy single-value duration if the input doesn't match
  // 30/60/90 (e.g. legacy quotes saved before the upgrade).
  const aiMap = { 30: ["AI Motion 30s", dAI30], 60: ["AI Motion 60s", dAI60], 90: ["AI Motion 90s", dAI90] };
  const cineMap = { 30: ["Cinematic Film 30s", dCine30], 60: ["Cinematic Film 60s", dCine60], 90: ["Cinematic Film 90s", dCine90] };
  // Several videos of one kind are produced one after another, so a kind's
  // days are the sum of its videos; the two kinds still run in parallel.
  const videoRows = (enabled, videos, legacySeconds, map, fallback) => {
    if (!enabled) return [];
    return groupedVideos(normalizeVideos(videos, legacySeconds))
      .map(({ sec, qty }) => {
        const [name, days] = map[sec] || fallback;
        return { name, qty, days: days * qty, lane: "video", seconds: sec };
      })
      .filter((r) => r.days > 0);
  };
  const aiRows = videoRows(items.aiMotion, items.aiMotionVideos, items.aiMotionDur, aiMap, ["AI Motion Video", dAI]);
  const cineRows = videoRows(items.cinematic, items.cinematicVideos, items.cinematicDur, cineMap, ["Cinematic Animation", dCine]);
  const sumDays = (rows) => rows.reduce((s, r) => s + r.days, 0);
  const laneC = Math.max(sumDays(aiRows), sumDays(cineRows));
  active.push(...aiRows, ...cineRows);

  // Lane D — Floor plans parallel
  const laneD = Math.max(
    items.total2D > 0 ? d2D * items.total2D : 0,
    items.total3D > 0 ? d3D * items.total3D : 0,
  );
  if (items.total2D > 0) active.push({ name: "2D Floor Plan", qty: items.total2D, days: d2D * items.total2D, lane: "floorplans" });
  if (items.total3D > 0) active.push({ name: "3D Floor Plan", qty: items.total3D, days: d3D * items.total3D, lane: "floorplans" });

  const lanes = { renderings: laneA, vr: laneB, video: laneC, floorplans: laneD };
  const longestEntry = Object.entries(lanes).reduce((acc, kv) => (kv[1] > acc[1] ? kv : acc), ["renderings", -1]);
  const laneLabels = {
    renderings: "Lane A (Renderings)",
    vr: "Lane B (VR Tours)",
    video: "Lane C (Video)",
    floorplans: "Lane D (Floor Plans)",
  };

  // Apply staffing concurrency cap to Studio Viz lanes.
  // Mirrors backend _effective_phase_days: with cap artists, the parallel
  // lanes are scheduled so that total = max(longest, ceil(totalWork / cap)).
  const activeLaneDays = Object.values(lanes).filter((d) => d > 0);
  let vizDays;
  if (activeLaneDays.length === 0) {
    vizDays = 0;
  } else {
    const cap = maxArtists && maxArtists >= 1 ? maxArtists : activeLaneDays.length;
    const totalWork = activeLaneDays.reduce((s, d) => s + d, 0);
    const longest = Math.max(...activeLaneDays);
    const eff = Math.min(cap, activeLaneDays.length);
    vizDays = Math.max(longest, Math.ceil(totalWork / Math.max(1, eff)));
  }
  const total = RAMP_UP_BUFFER_DAYS + vizDays;

  return {
    days: total,
    active,
    lanes,
    longestLane: longestEntry[1] > 0 ? laneLabels[longestEntry[0]] : null,
    rampUpBuffer: RAMP_UP_BUFFER_DAYS,
  };
}

// ── Public API ───────────────────────────────────────────────────────
// `inputs` is the flat input bundle persisted to the quote (`payload.inputs`).
// `durations` defaults to the fallback map; pass the result of
// `fetchTimelineDurations()` for live-from-DB values.
export function computeVexosTimeline(inputs, projectType, durations, proposalTotal) {
  const dur = durations || TIMELINE_FALLBACK_DURATIONS;
  const items = buildItemsFromInputs(inputs || {}, projectType || "condo");
  const vexos = calcVexos(items, dur);
  const maxArtists = resolveStaffingAllowance(proposalTotal);
  const viz = calcViz(items, dur, maxArtists);
  const vizCapped = viz.days > VIZ_MAX_DAYS;
  const vizDays = vizCapped ? VIZ_MAX_DAYS : viz.days;
  return applyTimelineOverride({
    vexosBusinessDays: vexos.days,
    vexosNaturalDays: vexos.naturalDays,
    vexosMinimumDays: vexos.minimumDays,
    vexosMinimumApplied: vexos.floorApplied,
    vexosWeeksDisplay: daysToWeeksDisplay(vexos.days),
    vizBusinessDays: vizDays,
    vizWeeksDisplay: vizCapped ? `~${VIZ_MAX_WEEKS} weeks` : daysToWeeksDisplay(viz.days),
    vizCapApplied: vizCapped,
    vizUncappedBusinessDays: viz.days,
    activeVexosItems: vexos.active,
    activeVizItems: viz.active,
    longestVexosLane: vexos.longestLane,
    longestVizLane: viz.longestLane,
    rampUpBuffer: viz.rampUpBuffer,
    staffingAllowance: maxArtists,
    durations: dur,
  }, inputs?.timelineOverride);
}

// ── Rep timeline override ────────────────────────────────────────────
// Mirrors apply_timeline_override in backend/routers/vexos_timeline.py —
// the server applies it to the saved snapshot (and so the PDF and intake);
// this copy only keeps the first-paint card honest. Shape:
//   { viz: { minWeeks, maxWeeks } | null, vexos: { minWeeks, maxWeeks } | null }
export const TIMELINE_OVERRIDE_MAX_WEEKS = 104;

export function normalizeTrackOverride(raw) {
  if (!raw || typeof raw !== "object") return null;
  const toInt = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Math.round(Number(v));
    return Number.isFinite(n) && n >= 1 && n <= TIMELINE_OVERRIDE_MAX_WEEKS ? n : null;
  };
  let lo = toInt(raw.minWeeks);
  let hi = toInt(raw.maxWeeks);
  if (lo === null && hi === null) return null;
  lo = lo ?? hi;
  hi = hi ?? lo;
  if (lo > hi) [lo, hi] = [hi, lo];
  return { minWeeks: lo, maxWeeks: hi };
}

export function normalizeTimelineOverride(raw) {
  const viz = normalizeTrackOverride(raw?.viz);
  const vexos = normalizeTrackOverride(raw?.vexos);
  return viz || vexos ? { viz, vexos } : null;
}

export function overrideWeeksDisplay(ov) {
  return ov.minWeeks === ov.maxWeeks
    ? `${ov.minWeeks} week${ov.minWeeks > 1 ? "s" : ""}`
    : `${ov.minWeeks}–${ov.maxWeeks} weeks`;
}

export function applyTimelineOverride(timeline, override) {
  const out = { ...timeline };
  for (const track of ["viz", "vexos"]) {
    const ov = normalizeTrackOverride(override?.[track]);
    if (!ov) continue;
    out[`${track}CalculatedBusinessDays`] = timeline[`${track}BusinessDays`];
    out[`${track}CalculatedWeeksDisplay`] = timeline[`${track}WeeksDisplay`];
    out[`${track}BusinessDays`] = ov.maxWeeks * 5;
    out[`${track}WeeksDisplay`] = overrideWeeksDisplay(ov);
    out[`${track}Override`] = ov;
    if (track === "viz") out.vizCapApplied = false;
  }
  return out;
}
